import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ModuleProvider } from './contexts/ModuleContext';

// Pages
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import AuthCallback from './pages/AuthCallback';
import DashboardPage from './pages/DashboardPage';
import CreateTicketPage from './pages/CreateTicketPage';
import EditTicketPage from './pages/EditTicketPage';
import MyTicketsPage from './pages/MyTicketsPage';
import InformedRequestsPage from './pages/InformedRequestsPage';
import SpocManagementPage from './pages/SpocManagementPage';
import ClientVisitFeedbackPage from './pages/ClientVisitFeedbackPage';
import PublicFeedbackPage from './pages/PublicFeedbackPage';
import TicketDetailPage from './pages/TicketDetailPage';
import ApprovalsPage from './pages/ApprovalsPage';
import AdminQueuePage from './pages/AdminQueuePage';
import EmployeesPage from './pages/EmployeesPage';
import SettingsPage from './pages/SettingsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import AnalyticsPage from './pages/AnalyticsPage';
import RequestTypesPage from './pages/RequestTypesPage';
import ITRequestTypesPage from './pages/ITRequestTypesPage';
import ITQueuePage from './pages/ITQueuePage';
import ITDashboardPage from './pages/ITDashboardPage';
import EscalatedRequestsPage from './pages/EscalatedRequestsPage';
import BackupPage from './pages/BackupPage';
import BackupManagementPage from './pages/BackupManagementPage';
import SetupPasswordPage from './pages/SetupPasswordPage';
import RoomSchedulePage from './pages/RoomSchedulePage';
import LocationAccessPage from './pages/LocationAccessPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import FeedbackPage from './pages/FeedbackPage';
import DailyExpensesPage from './pages/DailyExpensesPage';
import ExpenseMastersPage from './pages/ExpenseMastersPage';
import BudgetRequestsPage from './pages/BudgetRequestsPage';
import ProcurementPage from './pages/ProcurementPage';
import CourierDetailsPage from './pages/CourierDetailsPage';
import SurveysPage from './pages/SurveysPage';
import SurveyBuilderPage from './pages/SurveyBuilderPage';
import SurveyDetailPage from './pages/SurveyDetailPage';
import SurveyFillPage from './pages/SurveyFillPage';
import SurveyPermissionsPage from './pages/SurveyPermissionsPage';
import MySurveysPage from './pages/MySurveysPage';
import MandatorySurveyPopup from './components/MandatorySurveyPopup';
import AnnouncementPopup from './components/AnnouncementPopup';
import InductionsPage from './pages/InductionsPage';
import InductionBuilderPage from './pages/InductionBuilderPage';
import InductionDetailPage from './pages/InductionDetailPage';
import MyInductionPage from './pages/MyInductionPage';
import InductionApprovalPage from './pages/InductionApprovalPage';
import InductionApprovalsPage from './pages/InductionApprovalsPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router - Handles session_id detection
const AppRouter = () => {
  const location = useLocation();
  const [surveyPhaseDone, setSurveyPhaseDone] = useState(false);

  // Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions - must happen BEFORE ProtectedRoute checks auth
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/setup-password" element={<SetupPasswordPage />} />
      <Route path="/induction-approval/:inductionId/:sessionId" element={<InductionApprovalPage />} />
      <Route path="/feedback/:token" element={<PublicFeedbackPage />} />
      
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tickets/create" element={
        <ProtectedRoute>
          <CreateTicketPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tickets/:ticketId/edit" element={
        <ProtectedRoute>
          <EditTicketPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tickets/:ticketId" element={
        <ProtectedRoute>
          <TicketDetailPage />
        </ProtectedRoute>
      } />
      
      <Route path="/tickets" element={
        <ProtectedRoute>
          <MyTicketsPage />
        </ProtectedRoute>
      } />

      <Route path="/informed-requests" element={
        <ProtectedRoute>
          <InformedRequestsPage />
        </ProtectedRoute>
      } />

      <Route path="/spoc-management" element={
        <ProtectedRoute>
          <SpocManagementPage />
        </ProtectedRoute>
      } />

      <Route path="/client-visit-feedback" element={
        <ProtectedRoute>
          <ClientVisitFeedbackPage />
        </ProtectedRoute>
      } />
      
      <Route path="/approvals" element={
        <ProtectedRoute>
          <ApprovalsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/admin/queue" element={
        <ProtectedRoute>
          <AdminQueuePage />
        </ProtectedRoute>
      } />

      <Route path="/it/queue" element={
        <ProtectedRoute>
          <ITQueuePage />
        </ProtectedRoute>
      } />

      <Route path="/it/dashboard" element={
        <ProtectedRoute>
          <ITDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/it/escalated" element={
        <ProtectedRoute>
          <EscalatedRequestsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/employees" element={
        <ProtectedRoute>
          <EmployeesPage />
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      } />
      
      <Route path="/analytics" element={
        <ProtectedRoute>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      
      <Route path="/request-types" element={
        <ProtectedRoute>
          <RequestTypesPage />
        </ProtectedRoute>
      } />

      <Route path="/it-request-types" element={
        <ProtectedRoute>
          <ITRequestTypesPage />
        </ProtectedRoute>
      } />

      <Route path="/location-access" element={
        <ProtectedRoute>
          <LocationAccessPage />
        </ProtectedRoute>
      } />
      
      <Route path="/backup" element={
        <ProtectedRoute>
          <BackupPage />
        </ProtectedRoute>
      } />
      
      <Route path="/backup-management" element={
        <ProtectedRoute>
          <BackupManagementPage />
        </ProtectedRoute>
      } />
      
      <Route path="/room-schedule" element={
        <ProtectedRoute>
          <RoomSchedulePage />
        </ProtectedRoute>
      } />

      <Route path="/announcements" element={
        <ProtectedRoute>
          <AnnouncementsPage />
        </ProtectedRoute>
      } />

      <Route path="/feedback" element={
        <ProtectedRoute>
          <FeedbackPage />
        </ProtectedRoute>
      } />

      <Route path="/daily-expenses" element={
        <ProtectedRoute>
          <DailyExpensesPage />
        </ProtectedRoute>
      } />

      <Route path="/courier-details" element={
        <ProtectedRoute>
          <CourierDetailsPage />
        </ProtectedRoute>
      } />

      <Route path="/surveys" element={<ProtectedRoute><SurveysPage /></ProtectedRoute>} />
      <Route path="/my-surveys" element={<ProtectedRoute><MySurveysPage /></ProtectedRoute>} />
      <Route path="/surveys/new" element={<ProtectedRoute><SurveyBuilderPage /></ProtectedRoute>} />
      <Route path="/surveys/permissions" element={<ProtectedRoute><SurveyPermissionsPage /></ProtectedRoute>} />
      <Route path="/surveys/:surveyId" element={<ProtectedRoute><SurveyDetailPage /></ProtectedRoute>} />
      <Route path="/surveys/:surveyId/edit" element={<ProtectedRoute><SurveyBuilderPage /></ProtectedRoute>} />
      <Route path="/surveys/:surveyId/fill" element={<ProtectedRoute><SurveyFillPage /></ProtectedRoute>} />

      <Route path="/inductions" element={<ProtectedRoute><InductionsPage /></ProtectedRoute>} />
      <Route path="/inductions/new" element={<ProtectedRoute><InductionBuilderPage /></ProtectedRoute>} />
      <Route path="/inductions/:inductionId" element={<ProtectedRoute><InductionDetailPage /></ProtectedRoute>} />
      <Route path="/inductions/:inductionId/edit" element={<ProtectedRoute><InductionBuilderPage /></ProtectedRoute>} />
      <Route path="/my-induction" element={<ProtectedRoute><MyInductionPage /></ProtectedRoute>} />
      <Route path="/induction-approvals" element={<ProtectedRoute><InductionApprovalsPage /></ProtectedRoute>} />

      <Route path="/expense-masters" element={
        <ProtectedRoute>
          <ExpenseMastersPage />
        </ProtectedRoute>
      } />

      <Route path="/budget-requests" element={
        <ProtectedRoute>
          <BudgetRequestsPage />
        </ProtectedRoute>
      } />

      <Route path="/procurement" element={
        <ProtectedRoute>
          <ProcurementPage />
        </ProtectedRoute>
      } />
      
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    <MandatorySurveyPopup onResolved={() => setSurveyPhaseDone(true)} />
    <AnnouncementPopup active={surveyPhaseDone} />
    </>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ModuleProvider>
          <NotificationProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
          </NotificationProvider>
        </ModuleProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
